import React, { useState, useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import Customer, * as Cust from 'data/Customer';
import InfoAndCompanies from 'common/Customers/InfoAndCompanies';
import CommissionOrders, { CommissionActionButtons } from './CommissionOrders';
import PaymentHistory from './PaymentHistory';
import EditorSection from 'view/EditorSection';
import EditorNameInput from 'view/EditorNameInput';
import Page from 'components/UI/Page';
import Tabs from 'components/UI/Tabs';
import Button from 'components/UI/Button';
import { renderToast, toast } from 'components/UI/ToastNotification';
import { Color, FontSize, FontWeight } from 'lib/styles';
import { useAPI } from 'lib/API';
import { Background, Main } from 'styles/themeColors';
import { Space } from 'styles/themeSpaces';
import { loadCustomerAffiliates, loadCommissionData } from 'requests/affiliateMember';
import { AffiliateListItem, AffiliateCommissionData, CommissionDataResponse, CustomerAffiliatesResponse } from 'data/AffiliateMember';
import SelectComponent from 'components/UI/Select';
import { formatDateShort, currencySign } from 'utils/formatters';
import { loadActiveAffiliateConsignment } from 'requests/customer';
import { navigate } from 'hooks/useHookRouterCompat';
import { TEXT_LABELS } from 'constants/order';
import { COUNTRY_ISO } from 'constants/country';
import FeatureFlag from 'data/FeatureFlag';
import Vendor from 'data/Vendor';
import { BusinessUnit } from 'data/BusinessUnit';
import { AFFILIATE_MEMBER_TABS, AFFILIATE_MEMBER_STATUS, AFFILIATE_STRINGS } from 'constants/affiliate';
import { PermissionCode } from 'data/UserPermissions';
import { useBusinessUnit } from 'context/businessUnit';
import { useQueryParams } from 'hooks/useHookRouterCompat';

const EmptyMessage = styled.span`
  font-size: 24px;
`;

const NoBorderWrapper = styled.div`
  & > section {
    border-bottom: none;
  }
`;

const CommissionTotalRow = styled.div`
  display: flex;
  align-items: center;
  gap: 60px;
  padding: 20px 0;
  margin-top: 20px;
`;

const CommissionTotalLabel = styled.span`
  font-size: ${FontSize.large};
  font-weight: ${FontWeight.bold};
`;

const CommissionTotalValue = styled.span`
  font-size: ${FontSize.large};
  font-weight: ${FontWeight.bold};
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${Space.sp2x};
  flex: 1;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${Space.sp2xPlus};
  margin-left: auto;
`;

const SelectionInfo = styled.span`
  font-size: 16px;
  color: ${Main.brandDark};
  width: max-content;
`;

const ProgramHistoryContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Space.spBase};
  white-space: nowrap;
`;

const buildDropdownLabel = (a: AffiliateListItem): string => {
  const deactivatedDate = a.deactivatedOn ?? a.updatedOn;
  if (a.status === AFFILIATE_MEMBER_STATUS.INACTIVE && deactivatedDate) {
    return `${a.affiliateCode} - (Deactivated ${formatDateShort(deactivatedDate)})`;
  }
  return a.affiliateCode;
};

interface Props {
  memberId: string;
  featureFlags: FeatureFlag;
  selectedVendor?: Vendor;
  selectedBusinessUnit?: BusinessUnit;
}

const MemberDetails = ({ memberId, featureFlags, selectedVendor, selectedBusinessUnit }: Props) => {
  const [queryParams, setParams] = useQueryParams();
  const [memberDetails, setMemberDetails] = useState<Customer>({ id: 0, ...Cust.empty });
  const [activeTab, setActiveTab] = useState<number>(() => {
    const tabParam = typeof queryParams.tab === 'string' ? parseInt(queryParams.tab, 10) : NaN;
    if (!isNaN(tabParam) && tabParam >= 0 && tabParam < AFFILIATE_MEMBER_TABS.length) {
      return tabParam;
    }
    if (queryParams.consignmentIds || queryParams.commissionStatus) {
      return 1;
    }
    return 0;
  });
  const [actionButtons, setActionButtons] = useState<CommissionActionButtons | null>(null);
  const [activeAffiliateConsignment, setActiveAffiliateConsignment] = useState<{
    hasActiveAffiliateConsignment: boolean;
    consignmentId?: string;
  }>({ hasActiveAffiliateConsignment: false });
  const [commissionBalance, setCommissionBalance] = useState<number>(0);
  const [affiliateData, setAffiliateData] = useState<AffiliateListItem[] | null>(null);
  const [commissionData, setCommissionData] = useState<AffiliateCommissionData[] | null>(null);
  const [selectedAffiliateId, setSelectedAffiliateId] = useState<number | null>(null);

  const brandCode = selectedBusinessUnit?.brand?.code || '';
  const country = memberDetails.companies?.[0]?.companyCountry?.countryIso || COUNTRY_ISO.US;

  const { businessUnitList, updateBusinessUnit } = useBusinessUnit();

  // Auto-select business unit from brandCode query param
  useEffect(() => {
    const brandCodeParam = typeof queryParams.brandCode === 'string' ? queryParams.brandCode : '';
    if (brandCodeParam && businessUnitList.length > 0) {
      const matchingUnit = businessUnitList.find(bu => bu.brand?.code === brandCodeParam);
      if (matchingUnit && selectedBusinessUnit?.id !== matchingUnit.id) {
        updateBusinessUnit(String(matchingUnit.id));
      }
    }
  }, [queryParams.brandCode, businessUnitList, selectedBusinessUnit?.id, updateBusinessUnit]);

  const consignmentIdsParam = typeof queryParams.consignmentIds === 'string' ? queryParams.consignmentIds : '';

  // Fetches the slim affiliate list for the dropdown
  const affiliatesReq = useAPI({
    deferFn: loadCustomerAffiliates,
    onResolve: (result: CustomerAffiliatesResponse) => {
      const affiliates = result.affiliates || [];
      setAffiliateData(affiliates.length > 0 ? affiliates : null);
      if (affiliates.length > 0) {
        const active = affiliates.find(a => a.status === AFFILIATE_MEMBER_STATUS.ACTIVE);
        const mostRecent = affiliates.reduce((latest, a) =>
          a.customerAffiliateId > latest.customerAffiliateId ? a : latest
        );
        setSelectedAffiliateId((active ?? mostRecent).customerAffiliateId);
      }
    },
    onReject: (err: Error) => renderToast(toast.TYPE.ERROR, err.message)
  });

  useEffect(() => {
    if (!brandCode) return;
    affiliatesReq.run(memberId, brandCode);
  }, [memberId, brandCode]);

  // Fetches commission data (stats + transactions) for the selected affiliate
  const commissionDataReq = useAPI({
    deferFn: loadCommissionData,
    onResolve: (result: CommissionDataResponse) => {
      setMemberDetails(result.customer as any);
      setCommissionBalance(result.commissionBalance || 0);
      const affiliates = result.affiliates || [];
      setCommissionData(affiliates.length > 0 ? affiliates : null);
    },
    onReject: (err: Error) => renderToast(toast.TYPE.ERROR, err.message)
  });

  useEffect(() => {
    if (!selectedAffiliateId) return;
    commissionDataReq.run(memberId, selectedAffiliateId);
  }, [selectedAffiliateId, memberId]);

  const activeAffiliateReq = useAPI({
    deferFn: loadActiveAffiliateConsignment,
    onResolve: (result: any) => setActiveAffiliateConsignment(result),
    onReject: (err: Error) => renderToast(toast.TYPE.ERROR, err.message)
  });

  useEffect(() => {
    if (selectedBusinessUnit?.id) {
      activeAffiliateReq.run(memberId, selectedBusinessUnit.id);
    }
  }, [selectedBusinessUnit?.id]);

  const handleActionButtonsChange = useCallback((buttons: CommissionActionButtons | null) => {
    setActionButtons(buttons);
  }, []);

  const handleTabChange = useCallback((tab: number) => {
    setActiveTab(tab);
    if (tab === 0) {
      setParams({ commissionStatus: undefined, consignmentIds: undefined, tab: undefined });
      if (selectedAffiliateId) commissionDataReq.run(memberId, selectedAffiliateId);
    }
  }, [setParams, brandCode, memberId]);
  const selectedAffiliate = useMemo(
    () => affiliateData?.find(a => a.customerAffiliateId === selectedAffiliateId) ?? null,
    [affiliateData, selectedAffiliateId]
  );

  const affiliateOptions = useMemo(
    () => (affiliateData || []).map(a => ({
      value: String(a.customerAffiliateId),
      label: buildDropdownLabel(a),
      id: String(a.customerAffiliateId)
    })),
    [affiliateData]
  );

  const handleAffiliateChange = useCallback((e: any) => {
    const id = Number(e.target.id);
    if (id) setSelectedAffiliateId(id);
  }, []);

  if (!selectedBusinessUnit) {
    return <Page bgColor={Background.white} padding="10px 30px"><EmptyMessage>No business unit selected</EmptyMessage></Page>;
  }

  const isLoading = affiliatesReq.isLoading || commissionDataReq.isLoading;

  return (
    <Page bgColor={Background.white} padding="10px 30px" isLoading={isLoading}>
      <EditorSection disablePaddingBottom={true} noBorder>
        <NameRow>
          <EditorNameInput
            onChange={() => { }}
            placeholder={AFFILIATE_STRINGS.pageTitles.memberDetails}
            value={`${memberDetails.firstName} ${memberDetails.lastName}`.trim()}
            fontSize={FontSize.huge}
            showDelimiter={false}
            disabled
            disabledBgColor={Background.white}
            disabledColor={Color.black}
            className="heading"
          />
          {affiliateData && affiliateData.length > 0 && (
            <ProgramHistoryContainer>
              <SelectionInfo>{AFFILIATE_STRINGS.labels.affiliateProgramHistory}:</SelectionInfo>
              <SelectComponent
                value={selectedAffiliateId != null ? String(selectedAffiliateId) : ''}
                options={affiliateOptions}
                onChange={handleAffiliateChange}
                width="100%"
              />
            </ProgramHistoryContainer>
          )}
          {activeAffiliateConsignment.hasActiveAffiliateConsignment
            && selectedAffiliate?.status === AFFILIATE_MEMBER_STATUS.ACTIVE
            && activeTab === 0 && (
            <Button
              bgColor={Main.error2}
              borderColor={Main.error2}
              width='max-content'
              onClick={() => navigate(`/orders/order-details/${activeAffiliateConsignment.consignmentId!.slice(4)}`)}
            >
              {TEXT_LABELS.DEACTIVATE_AFFILIATE}
            </Button>
          )}
          {actionButtons && (
            <Actions>
              <SelectionInfo>
                Total Selected: {currencySign(country, actionButtons.commissionSum)}
              </SelectionInfo>
              {actionButtons.canApprove && (
                <Button
                  bgColor={Main.brandDark}
                  borderColor={Main.brandDark}
                  disabled={actionButtons.isLoading}
                  onClick={actionButtons.onApprove}
                  width='max-content'
                  permissionCode={PermissionCode.APPROVE_COMMISSION}
                >
                  {AFFILIATE_STRINGS.buttons.approveSelected}
                </Button>
              )}
              {actionButtons.canUnapprove && (
                <Button
                  bgColor={Main.error}
                  borderColor={Main.error}
                  disabled={actionButtons.isLoading}
                  onClick={actionButtons.onUnapprove}
                  width='max-content'
                  permissionCode={PermissionCode.UNAPPROVE_COMMISSION}
                >
                  {AFFILIATE_STRINGS.buttons.unapproveSelected}
                </Button>
              )}
              {actionButtons.canPay && (
                <Button
                  bgColor={Main.brandDark}
                  borderColor={Main.brandDark}
                  disabled={actionButtons.isLoading}
                  onClick={actionButtons.onPay}
                  width='max-content'
                  permissionCode={PermissionCode.PAY_COMMISSION}
                >
                  {AFFILIATE_STRINGS.buttons.paySelected}
                </Button>
              )}
            </Actions>
          )}
        </NameRow>
      </EditorSection>
      <Tabs tabs={AFFILIATE_MEMBER_TABS} activeTab={activeTab} setActiveTab={handleTabChange}>
        <>
          <NoBorderWrapper>
            <InfoAndCompanies customerDetails={memberDetails} />
          </NoBorderWrapper>
          <CommissionTotalRow>
            <CommissionTotalLabel>Commission total</CommissionTotalLabel>
            <CommissionTotalValue>{currencySign(country, commissionBalance)}</CommissionTotalValue>
          </CommissionTotalRow>
          <PaymentHistory affiliates={commissionData} country={country} />
        </>
        <CommissionOrders
          customerId={Number(memberId)}
          brandCode={brandCode}
          selectedVendor={selectedVendor}
          selectedBusinessUnit={selectedBusinessUnit}
          featureFlags={featureFlags}
          initialAffiliateData={commissionData}
          onActionButtonsChange={handleActionButtonsChange}
          consignmentIds={consignmentIdsParam}
          selectedAffiliateId={selectedAffiliateId}
        />
      </Tabs>
    </Page>
  );
};

export default MemberDetails;